import * as yup from "yup";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ACTIVE_COLOR } from "@/constants/color";
import { User } from "@/types/user";
import { useEffect } from "react";
import { useRoles } from "@/services/useRoles";
import create from "@/services/users/create";
import update from "@/services/users/update";
import { useUsers } from "@/hooks/useUsers";
import { useProjects } from "@/services/useProjects";
import {
  ASESOR_CIERRE_ROLE,
  ASESOR_ROLE,
  CORDINATOR_ROLE,
  MASTER_ROLE,
  REGIONAL_PRINCIPAL_ROLE,
  SUPERVISOR_ROLE,
  GENERAL_PRINCIPAL_ROLE,
} from "@/constants/roles";
import { useAuth } from "@/context/auth-context";
import { useRegions } from "@/services/useRegions";

export const getRole = (role: string) => {
  const isAsesor = [ASESOR_CIERRE_ROLE, ASESOR_ROLE].includes(role);
  const isRegionalPrincipal = [REGIONAL_PRINCIPAL_ROLE].includes(role);
  const isAdmin = [GENERAL_PRINCIPAL_ROLE].includes(role);
  const isMaster = [MASTER_ROLE].includes(role);
  const isCordinator = [CORDINATOR_ROLE, SUPERVISOR_ROLE].includes(role);

  return {
    isAdmin,
    isAsesor,
    isMaster,
    isCordinator,
    isRegionalPrincipal,
  };
};

interface UserDrawerProps {
  open: boolean;
  onClose: () => void;
  user?: Partial<User> | null;
}

export const UserDrawer: React.FC<UserDrawerProps> = ({
  open,
  onClose,
  user,
}) => {
  const theme = useTheme();
  const { refetch } = useUsers();
  const { data: roles } = useRoles();
  const { data: campaigns } = useProjects();
  const { data: users } = useUsers();
  const { data: regions } = useRegions();

  const schema = yup.object({
    username: yup.string().required("Usuario requerido"),
    password: yup.string().when([], {
      is: () => !user || !user._id,
      then: (schema) => schema.required("Contraseña requerida"),
      otherwise: (schema) => schema.notRequired(),
    }),
    email: yup.string().email("Email inválido").required("Email requerido"),
    role: yup
      .object()
      .shape({
        _id: yup.string().required(),
        name: yup.string().required(),
      })
      .nullable()
      .required("Rol requerido"),
    name: yup.string(),
    campaigns: yup.array().of(yup.string()).optional(),
    users: yup.array().of(yup.string()).optional(),
    cordinations: yup.array().of(yup.string()).optional(),
    lastName: yup.string(),
    phone: yup.string(),
    region: yup.string(),
  });

  type FormData = yup.InferType<typeof schema>;

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      username: "",
      password: "",
      email: "",
      role: undefined,
      name: "",
      campaigns: [],
      users: [],
      cordinations: [],
      lastName: "",
      phone: "",
      region: "",
    },
  });

  const handleClose = () => {
    reset({
      username: "",
      password: "",
      email: "",
      role: undefined,
      name: "",
      lastName: "",
      campaigns: [],
      users: [],
      cordinations: [],
      phone: "",
      region: "",
    });
    onClose();
  };

  const handleFormSubmit = async (data: FormData) => {
    if (!user) {
      await create(data);
    } else {
      await update(user._id!, data);
    }
    refetch();
    handleClose();
  };

  useEffect(() => {
    if (user) {
      reset({
        ...user,
        cordinations: user.cordinations?.map((c) => c._id) || [],
        campaigns: user.campaigns?.map((c) => c._id),
      });
    }
  }, [user]);

  const name = watch("name");
  const lastName = watch("lastName");
  const role = watch("role");

  useEffect(() => {
    setValue("username", `${name?.toLowerCase()} ${lastName?.toLowerCase()}`);
  }, [name, lastName]);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { isRegionalPrincipal, isAsesor, isMaster, isCordinator } = getRole(
    role?._id || ""
  );
  return (
    <Drawer
      anchor="right"
      open={open}
      PaperProps={{
        sx: {
          width: isMobile ? "80vw" : "30vw",
          maxWidth: isMobile ? "80vw" : "30vw",
        },
      }}
      onClose={handleClose}
    >
      <Box sx={{ width: "93%", p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" sx={{ mb: 2 }} fontWeight="bold">
            {user ? "Editar Usuario" : "Agregar Usuario"}
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(handleFormSubmit)}
          mt={2}
          display="flex"
          flexDirection="column"
          gap={2}
        >
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                label="Nombre"
                fullWidth
                {...field}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
              />
            )}
          />

          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                label="Apellido"
                fullWidth
                {...field}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
              />
            )}
          />
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <TextField
                label="Usuario"
                fullWidth
                error={!!errors.username}
                helperText={errors.username?.message}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                {...field}
                disabled
                value={`${name?.toLowerCase()} ${lastName?.toLowerCase()}`}
              />
            )}
          />

          {!user && (
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Contraseña"
                  type="password"
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                  {...field}
                />
              )}
            />
          )}

          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                label="Correo electrónico"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                {...field}
              />
            )}
          />

          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={(roles || []).filter((role) => {
                  if (isAsesor) return false;
                  if (isCordinator)
                    return [
                      ASESOR_ROLE,
                      ASESOR_CIERRE_ROLE,
                      MASTER_ROLE,
                    ].includes(role._id);
                  if (isMaster)
                    return [ASESOR_ROLE, ASESOR_CIERRE_ROLE].includes(role._id);
                  if (isRegionalPrincipal)
                    return [
                      ASESOR_ROLE,
                      ASESOR_CIERRE_ROLE,
                      MASTER_ROLE,
                      REGIONAL_PRINCIPAL_ROLE,
                      CORDINATOR_ROLE,
                    ].includes(role._id);
                  return true;
                })}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                onChange={(_, value) => field.onChange(value)}
                value={field.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rol"
                    error={!!errors.role}
                    helperText={errors.role?.message}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                  />
                )}
              />
            )}
          />

          <Controller
            name="cordinations"
            control={control}
            render={({ field }) => {
              const coordinatorOptions =
                users
                  .filter((u) => u.role._id === CORDINATOR_ROLE)
                  .map((u) => ({
                    name: `${u.name} ${u.lastName}`,
                    _id: u._id,
                  })) || [];

              return (
                <Autocomplete
                  multiple
                  options={coordinatorOptions}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                  onChange={(_, value) =>
                    field.onChange(value.map((v) => v._id))
                  }
                  value={coordinatorOptions.filter((opt) =>
                    field.value?.includes(opt._id)
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Coordinación"
                      error={!!errors.cordinations}
                      helperText={errors.cordinations?.message}
                      InputLabelProps={{ shrink: true }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                    />
                  )}
                />
              );
            }}
          />

          <Controller
            name="campaigns"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={campaigns.map((campaign) => ({
                  _id: campaign._id,
                  name: campaign.name,
                }))}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                value={
                  campaigns.filter((c) => field.value?.includes(c._id)) || []
                }
                onChange={(_, value) => field.onChange(value.map((v) => v._id))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Desarrollos"
                    error={!!errors.campaigns}
                    helperText={errors.campaigns?.message}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                  />
                )}
              />
            )}
          />

          {((isAsesor || isCordinator) && (
            <Controller
              name="users"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={users
                    .filter((u) => {
                      if (isAsesor) return String(u.role._id) === MASTER_ROLE;
                      if (isCordinator)
                        return String(u.role._id) === REGIONAL_PRINCIPAL_ROLE;
                      return true;
                    })
                    .map((user) => ({
                      _id: user._id,
                      name: user.name,
                      lastName: user.lastName,
                    }))}
                  getOptionLabel={(option) =>
                    `${option.name} ${option.lastName}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                  value={
                    users.filter((c) => field.value?.includes(c._id)) || []
                  }
                  onChange={(_, value) =>
                    field.onChange(value.map((v) => v._id))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={isAsesor ? "Master" : "Director Regional"}
                      error={!!errors.users}
                      helperText={errors.users?.message}
                      InputLabelProps={{ shrink: true }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                    />
                  )}
                />
              )}
            />
          )) ||
            null}

          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField
                label="Teléfono"
                fullWidth
                {...field}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
              />
            )}
          />

          <Controller
            name="region"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={regions.map((r) => r.name) || []}
                value={field.value}
                onChange={(_, value) => setValue("region", value || "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Región"
                    error={!!errors.region}
                    helperText={errors.region?.message}
                  />
                )}
              />
            )}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            sx={{ backgroundColor: ACTIVE_COLOR, borderRadius: 3 }}
            disabled={!isValid}
          >
            Guardar
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
